/**
 * <axiom-rail> — native horizontal scroll rail with drag-to-scroll on desktop,
 * previous/next buttons and a progress indicator.
 * Ported from Radian's component-horizontal-scroll-progress.
 */
(function () {
  'use strict';

  if (customElements.get('axiom-rail')) return;

  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

  class AxiomRail extends HTMLElement {
    connectedCallback() {
      this.rail = this.querySelector('.rail');
      if (!this.rail) return;
      this.bar = this.querySelector('.rail-progress');
      this.prev = this.querySelector('[data-rail-prev]');
      this.next = this.querySelector('[data-rail-next]');
      this.controller = new AbortController();
      const { signal } = this.controller;

      this.rail.addEventListener('scroll', () => this.requestUpdate(), { passive: true, signal });
      window.addEventListener('resize', () => this.requestUpdate(), { passive: true, signal });
      this.prev?.addEventListener('click', () => this.step(-1), { signal });
      this.next?.addEventListener('click', () => this.step(1), { signal });
      this.bindDrag(signal);
      this.requestUpdate();
    }

    disconnectedCallback() {
      this.controller?.abort();
      if (this.frame) cancelAnimationFrame(this.frame);
    }

    step(direction) {
      const item = this.rail.firstElementChild;
      const gap = parseFloat(getComputedStyle(this.rail).columnGap) || 0;
      const distance = item ? item.getBoundingClientRect().width + gap : this.rail.clientWidth * 0.8;
      this.rail.scrollBy({ left: distance * direction, behavior: 'smooth' });
    }

    bindDrag(signal) {
      let startX = 0;
      let startScroll = 0;
      let moved = false;
      let pointerId = null;

      this.rail.addEventListener(
        'pointerdown',
        (event) => {
          if (!finePointer.matches || event.button !== 0) return;
          pointerId = event.pointerId;
          startX = event.clientX;
          startScroll = this.rail.scrollLeft;
          moved = false;
        },
        { signal }
      );

      this.rail.addEventListener(
        'pointermove',
        (event) => {
          if (pointerId !== event.pointerId) return;
          const dx = event.clientX - startX;
          if (!moved && Math.abs(dx) > 6) {
            moved = true;
            this.rail.setPointerCapture(pointerId);
            this.rail.classList.add('is-dragging');
          }
          if (moved) this.rail.scrollLeft = startScroll - dx;
        },
        { signal }
      );

      const end = () => {
        if (pointerId === null) return;
        pointerId = null;
        if (!moved) return;
        this.rail.classList.remove('is-dragging');
        // Suppress the click that follows a drag.
        this.rail.addEventListener('click', (e) => e.preventDefault(), { capture: true, once: true });
      };
      this.rail.addEventListener('pointerup', end, { signal });
      this.rail.addEventListener('pointercancel', end, { signal });
    }

    requestUpdate() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.update();
      });
    }

    update() {
      const max = this.rail.scrollWidth - this.rail.clientWidth;
      const ratio = this.rail.scrollWidth ? this.rail.clientWidth / this.rail.scrollWidth : 1;
      const progress = max > 0 ? this.rail.scrollLeft / max : 0;
      const thumb = Math.max(ratio * 100, 8);
      if (this.bar) {
        this.bar.style.setProperty('--rail-thumb', thumb + '%');
        this.bar.style.setProperty('--rail-offset', ((progress * (100 - thumb)) / thumb) * 100 + '%');
      }
      this.toggleAttribute('data-scrollable', max > 2);
      if (this.prev) this.prev.disabled = this.rail.scrollLeft <= 2;
      if (this.next) this.next.disabled = this.rail.scrollLeft >= max - 2;
    }
  }

  customElements.define('axiom-rail', AxiomRail);
})();
